import { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  ACTIVE_TIMER_STORAGE_KEY,
  APP_FONTS,
  APP_LANGUAGES,
  APP_THEMES,
  APP_VISUAL_STYLES,
  APP_SETTINGS_STORAGE_KEY,
  APP_BUILD_ID,
  APP_VERSION,
  DATA_DISCLOSURE_CONSENT_STORAGE_KEY,
  AD_PERSONALIZATION_CONSENT_STORAGE_KEY,
  FRICTION_START_STORAGE_KEY,
  FRICTION_TYPES,
  IDLE_THRESHOLD_MS,
  INTERRUPTION_SOURCES,
  ONBOARDING_DONE_STORAGE_KEY,
  LEGAL_CONSENT_DONE_STORAGE_KEY,
  PARKING_DRAFT_STORAGE_KEY,
  STUCK_TIMER_HELPER_COUNT_STORAGE_KEY,
  TINY_WIN_DRAFT_STORAGE_KEY,
  UX_HINT_LEVEL_STORAGE_KEY,
  RESCUES
} from './constants';
import { exportDbSnapshot, getDb, getDbInitError, importDbSnapshot } from './db/client';
import {
  clearAllUserData,
  createAppSession,
  createIdleSegment,
  endAppSession,
  exportAllTables,
  listParkingLotEntries,
  listPings,
  listRescues,
  listRescueScripts,
} from './db/repo';
import { downloadBlob, parkingLotToCsv, pingsToCsv, rescuesToCsv, toJsonExport } from './lib/export';
import { createId } from './lib/id';
import { maybeShowInterstitialAd } from './lib/ads';
import {
  isAdConsentProviderAvailable,
  openAdPrivacyOptionsForm,
  readAdConsentState,
  refreshAdConsentProviderInfo,
  showAdConsentFormIfRequired
} from './lib/adConsent';
import { clearAppLocalState, personalizedAdsEnabled } from './lib/clientState';
import { listFavoriteRescues, recentContextForPingType, toggleFavoriteRescue } from './lib/rescuePreferences';
import {
  dayLabel,
  fontDescription,
  frictionSubtypeI18nLabel,
  frictionTypeLabel,
  interruptionLabel as interruptionSourceLabel,
  onboardingSteps,
  rangeLabel,
  rescueKindLabel,
  themeDescription,
  t
} from './i18n';
import { formatDuration, nowMs } from './lib/time';
import { useAppClientBootstrap, useAppClientPersistence } from './hooks/useAppClientState';
import { useAppText } from './hooks/useAppText';
import { useConsentController } from './hooks/useConsentController';
import { useJournalController } from './hooks/useJournalController';
import { useOptionsController } from './hooks/useOptionsController';
import {
  cancelNotification,
  checkExactAlarmPermission,
  listenForTimerNotificationActions,
  openExactAlarmSettings,
  requestTimerNotificationsPermission,
  requestReminderNotificationsPermission,
  scheduleReminderNotification,
  sendTimerDoneNotification
} from './lib/notifications';
import { personalizedTopPickRescueForFriction } from './lib/recommendation';
import { applyVisualStyleStylesheet } from './lib/designLanguage';
import { AppBottomNav } from './components/AppBottomNav';
import { ConsentFlow } from './components/ConsentFlow';
import { HistoryTab } from './components/HistoryTab';
import { InsightsTab } from './components/InsightsTab';
import { OptionsTab } from './components/OptionsTab';
import { ParkingLotModal } from './components/ParkingLotModal';
import { PingDetailModal } from './components/PingDetailModal';
import { PingRescueSheet } from './components/PingRescueSheet';
import { QuickLogSheet } from './components/QuickLogSheet';
import { RescuePicker } from './components/RescuePicker';
import { BreathingBox } from './components/BreathingBox';
import type {
  ActiveTimer,
  AppFont,
  AppLanguage,
  AppSettings,
  DateRange,
  FrictionSubtype,
  InterruptionSource,
  NotificationStyle,
  Ping,
  PingType,
  RescueKind,
  RescueOutcome,
  RescueSession,
  ParkingLotEntry
} from './types';

type Tab = 'log' | 'rescue' | 'history' | 'insights' | 'options';
const APP_THEME_SET = new Set(APP_THEMES.map((item) => item.theme));
const APP_VISUAL_STYLE_SET = new Set(APP_VISUAL_STYLES.map((item) => item.style));
const APP_FONT_SET = new Set(APP_FONTS.map((item) => item.font));
const APP_LANGUAGE_SET = new Set(APP_LANGUAGES.map((item) => item.language));
const EMPTY_RESCUE_SCRIPTS = RESCUES.reduce<Record<RescueKind, string>>((acc, rescue) => {
  acc[rescue.kind] = '';
  return acc;
}, {} as Record<RescueKind, string>);
const REENTRY_REMINDER_NOTIFICATION_ID = 910001;
const DAILY_REFLECTION_NOTIFICATION_ID = 910002;
const WEEKLY_PATTERN_NOTIFICATION_ID = 910003;
const REENTRY_REMINDER_DELAY_MS = 15 * 60 * 1000;

export default function App() {
  const defaultSettings: AppSettings = {
    showTips: true,
    defaultRescueSound: false,
    notificationStyle: 'quiet',
    reentryNudges: true,
    dailyReflectionPrompt: false,
    weeklyPatternPrompt: false,
    compactUi: false,
    simplifiedMode: true,
    confirmDelete: true,
    adsEnabled: true,
    adPersonalization: false,
    theme: 'warm_paper',
    visualStyle: 'soft_tactile',
    font: 'nunito',
    language: 'en'
  };
  const [tab, setTab] = useState<Tab>('log');
  const [storageError, setStorageError] = useState<string | null>(null);
  const [pings, setPings] = useState<Ping[]>([]);
  const [rescues, setRescues] = useState<RescueSession[]>([]);
  const [parkingEntries, setParkingEntries] = useState<ParkingLotEntry[]>([]);
  const [rescueScripts, setRescueScripts] = useState<Record<RescueKind, string>>(EMPTY_RESCUE_SCRIPTS);
  const [selectedPing, setSelectedPing] = useState<Ping | null>(null);
  const [selectedPingRescues, setSelectedPingRescues] = useState<RescueSession[]>([]);
  const [sheetPingId, setSheetPingId] = useState<string | null>(null);
  const [quickLogPingId, setQuickLogPingId] = useState<string | null>(null);
  const [quickLogPingType, setQuickLogPingType] = useState<PingType | null>(null);
  const [activeTimer, setActiveTimer] = useState<ActiveTimer | null>(null);
  const [timerNow, setTimerNow] = useState(nowMs());
  const [frictionStartMs, setFrictionStartMs] = useState<number | null>(null);
  const [undoPingId, setUndoPingId] = useState<string | null>(null);
  const [undoFrictionStartMs, setUndoFrictionStartMs] = useState<number | null>(null);
  const [undoUntil, setUndoUntil] = useState(0);
  const [historyRange, setHistoryRange] = useState<DateRange>('7d');
  const [insightRange, setInsightRange] = useState<DateRange>('30d');
  const [typeFilters, setTypeFilters] = useState<PingType[]>([]);
  const [bucketInfo, setBucketInfo] = useState<{ dow: number; hour: number; count: number } | null>(null);
  const [tinyWinRescueId, setTinyWinRescueId] = useState<string | null>(null);
  const [tinyWinPingId, setTinyWinPingId] = useState<string | null>(null);
  const [tinyWinSavedAt, setTinyWinSavedAt] = useState(0);
  const [tinyWinDraft, setTinyWinDraft] = useState('');
  const [tinyWinOutcome, setTinyWinOutcome] = useState<RescueOutcome | null>(null);
  const [parkingDraft, setParkingDraft] = useState('');
  const [parkingOpen, setParkingOpen] = useState(false);
  const [favoriteRescues, setFavoriteRescues] = useState<RescueKind[]>(() => listFavoriteRescues());
  const [metaSavedAt, setMetaSavedAt] = useState(0);
  const [hintLevel, setHintLevel] = useState(0);
  const [stuckTimerHelperCount, setStuckTimerHelperCount] = useState(0);
  const [settings, setSettings] = useState<AppSettings>(defaultSettings);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showLegalConsent, setShowLegalConsent] = useState(false);
  const [showDataDisclosure, setShowDataDisclosure] = useState(false);
  const [showAdConsent, setShowAdConsent] = useState(false);
  const [pendingPersonalizedAds, setPendingPersonalizedAds] = useState(false);
  const [adConsentStatus, setAdConsentStatus] = useState<'unknown' | 'accepted' | 'rejected' | 'not_required'>('not_required');
  const [adConsentProviderAvailable, setAdConsentProviderAvailable] = useState(false);
  const [adPrivacyOptionsAvailable, setAdPrivacyOptionsAvailable] = useState(false);
  const [exactAlarmStatus, setExactAlarmStatus] = useState<'granted' | 'denied' | 'unknown'>('unknown');
  const [legalDoc, setLegalDoc] = useState<'privacy' | 'terms' | null>(null);
  const [onboardingStep, setOnboardingStep] = useState(0);

  const idleAtRef = useRef<number | null>(null);
  const lastInteractionRef = useRef<number>(Date.now());
  const notifiedTimerIdRef = useRef<string | null>(null);
  const previousTabRef = useRef<Tab>('log');
  const completeTimerFromNotificationRef = useRef<() => void>(() => undefined);
  const snoozeTimerFromNotificationRef = useRef<() => void>(() => undefined);
  const handledLaunchActionRef = useRef(false);
  const landingIntroFlowRef = useRef(false);

  useEffect(() => {
    void (async () => {
      try {
        await getDb();
        await refreshData();
      } catch {
        setStorageError(getDbInitError() ?? t(defaultSettings.language, 'app.storage_unavailable'));
      }
    })();
  }, []);

  useAppClientBootstrap({
    defaultSettings,
    appThemeSet: APP_THEME_SET,
    appVisualStyleSet: APP_VISUAL_STYLE_SET,
    appFontSet: APP_FONT_SET,
    appLanguageSet: APP_LANGUAGE_SET,
    setActiveTimer,
    setFrictionStartMs,
    setTinyWinDraft,
    setParkingDraft,
    setHintLevel,
    setStuckTimerHelperCount,
    setAdConsentStatus,
    setShowLegalConsent,
    setShowDataDisclosure,
    setShowOnboarding,
    setSettings
  });

  useEffect(() => {
    const id = window.setInterval(() => setTimerNow(nowMs()), 500);
    return () => window.clearInterval(id);
  }, []);

  useAppClientPersistence({
    activeTimer,
    frictionStartMs,
    tinyWinDraft,
    parkingDraft,
    settings,
    adConsentStatus
  });

  const language = settings.language as AppLanguage;
  const {
    tr,
    interruptionLabel,
    subtypeLabel,
    pingTypeLabel,
    rescueKindText,
    activeThemeDescription,
    activeVisualStyleDescription,
    activeFontDescription,
    steps,
    optionsLabels,
    insightsLabels,
    rescuePickerLabels,
    bottomNavLabels,
    pingRescueSheetLabels,
    quickLogLabels,
    pingDetailLabels,
    parkingLabels,
    consentLabels,
    rangeLabel,
    dayLabel: appDayLabel,
    prepItemLabel,
    rescueGuide
  } = useAppText({
    language,
    theme: settings.theme,
    visualStyle: settings.visualStyle,
    font: settings.font,
    onboardingStep
  });
  const resetTransientUiAfterImport = async (): Promise<void> => {
    await refreshData();
    setSelectedPing(null);
    setSelectedPingRescues([]);
    setSheetPingId(null);
    setQuickLogPingId(null);
    setQuickLogPingType(null);
  };
  const {
    backupStatus,
    setBackupStatus,
    backupStatusTone,
    setBackupStatusTone,
    syncActivity,
    setSyncActivity,
    driveConnected,
    driveConfigured,
    entitlement,
    billingConfigured: billingEnabled,
    billingStatusText,
    setBillingStatusText,
    monetizationPlans,
    canManageBilling,
    billingDiagnostics,
    clearBillingDiagnosticsEvents,
    createBackup,
    restoreBackup,
    connectDrive,
    disconnectDrive,
    uploadDriveBackup,
    restoreDriveBackup,
    checkBilling,
    restoreBilling,
    buyPlan,
    manageBilling,
    runBillingDiagnostics,
    resetOptionsState
  } = useOptionsController({
    tab,
    tr,
    openUrl: openExternalUrl,
    onAfterImportRestore: resetTransientUiAfterImport
  });
  const effectiveTier = entitlement.tier;
  const {
    updateAdPersonalization,
    openAdPrivacyOptions,
    completeOnboarding,
    completeLegalConsent,
    completeDataDisclosure,
    replayOnboarding,
    exitFromLegalConsent,
    openLegalDoc,
    closeLegalDoc,
    withdrawAdConsent,
    allowPersonalizedAds,
    keepNonPersonalizedAds
  } = useConsentController({
    adConsentStatus,
    adConsentProviderAvailable,
    showOnboarding,
    pendingPersonalizedAds,
    settings,
    tr,
    setSettings,
    setShowOnboarding,
    setShowLegalConsent,
    setShowDataDisclosure,
    setShowAdConsent,
    setPendingPersonalizedAds,
    setAdConsentStatus,
    setAdConsentProviderAvailable,
    setAdPrivacyOptionsAvailable,
    setLegalDoc,
    setOnboardingStep,
    setBillingStatusText
  });
  const personalizedAdsAllowed = personalizedAdsEnabled(settings.adPersonalization, {
    required: adConsentStatus !== 'not_required',
    status: adConsentStatus,
    updatedAtMs: 0
  });
  const isAdConsentRequired = adConsentStatus !== 'not_required';
  const {
    filteredHistory,
    repeatMinutes,
    handlePing,
    handleUndo,
    startRescue,
    completeTimer,
    cancelTimer,
    setInterruptionForPing,
    setSubtypeForPing,
    saveTinyWin,
    saveParkingLot,
    saveRescueScript,
    pauseResumeTimer,
    snoozeTimer,
    openPingDetail,
    addNoteToSelected,
    deleteSelectedPing,
    quickRepeatRescue
  } = useJournalController({
    settings,
    pings,
    rescues,
    historyRange,
    typeFilters,
    frictionStartMs,
    undoPingId,
    undoFrictionStartMs,
    undoUntil,
    activeTimer,
    tinyWinRescueId,
    tinyWinPingId,
    tinyWinDraft,
    tinyWinOutcome,
    parkingDraft,
    sheetPingId,
    quickLogPingId,
    quickLogPingType,
    selectedPing,
    notifiedTimerIdRef,
    setTab,
    setPings,
    setRescues,
    setRescueScripts,
    setSelectedPing,
    setSelectedPingRescues,
    setSheetPingId,
    setQuickLogPingId,
    setQuickLogPingType,
    setActiveTimer,
    setFrictionStartMs,
    setUndoPingId,
    setUndoFrictionStartMs,
    setUndoUntil,
    setTinyWinRescueId,
    setTinyWinPingId,
    setTinyWinDraft,
    setTinyWinOutcome,
    setTinyWinSavedAt,
    setParkingDraft,
    setParkingOpen,
    setMetaSavedAt,
    onAdvanceHintLevel: advanceHintLevel,
    onRefreshData: refreshData,
    tr,
    confirmDeletePing: () => !settings.confirmDelete || window.confirm(tr('app.confirm_delete_ping'))
  });

  useEffect(() => {
    const previous = previousTabRef.current;
    previousTabRef.current = tab;
    if (previous === 'insights' || tab !== 'insights') return;
    void maybeShowInterstitialAd({
      enabled: settings.adsEnabled && effectiveTier === 'free',
      personalized: personalizedAdsEnabled(settings.adPersonalization, {
        required: adConsentStatus !== 'not_required',
        status: adConsentStatus,
        updatedAtMs: 0
      }),
      placement: 'insights_open'
    });
  }, [adConsentStatus, effectiveTier, settings.adPersonalization, settings.adsEnabled, tab]);

  useEffect(() => {
    if (effectiveTier === 'free') return;
    setSettings((prev) => (prev.adsEnabled ? { ...prev, adsEnabled: false } : prev));
  }, [effectiveTier]);

  useEffect(() => {
    if (handledLaunchActionRef.current || Capacitor.isNativePlatform() || activeTimer) return;
    const params = new URLSearchParams(window.location.search);
    const requestedRescue = params.get('startRescue');
    if (requestedRescue !== 'reset_2m') return;

    handledLaunchActionRef.current = true;
    if (params.get('landingFlow') === '1') {
      landingIntroFlowRef.current = true;
    }
    void startRescue('reset_2m', null, null, false);

    params.delete('startRescue');
    params.delete('landingFlow');
    const nextQuery = params.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', nextUrl);
  }, [activeTimer, startRescue]);

  useEffect(() => {
    if (!isAdConsentRequired) return;
    if (adConsentStatus === 'accepted') return;
    if (!settings.adPersonalization) return;
    setSettings((prev) => ({ ...prev, adPersonalization: false }));
  }, [adConsentStatus, isAdConsentRequired, settings.adPersonalization]);

  useEffect(() => {
    document.body.dataset.theme = settings.theme;
    return () => {
      delete document.body.dataset.theme;
    };
  }, [settings.theme]);

  useEffect(() => {
    applyVisualStyleStylesheet(settings.visualStyle);
    document.body.dataset.visualStyle = settings.visualStyle;
    return () => {
      delete document.body.dataset.visualStyle;
    };
  }, [settings.visualStyle]);

  useEffect(() => {
    document.body.dataset.font = settings.font;
    return () => {
      delete document.body.dataset.font;
    };
  }, [settings.font]);

  useEffect(() => {
    if (!activeTimer || activeTimer.durationSec === 0) return;
    const remaining = getRemainingSec(activeTimer, timerNow);
    if (remaining > 0) return;
    if (notifiedTimerIdRef.current === activeTimer.rescueId) return;
    notifiedTimerIdRef.current = activeTimer.rescueId;
    void sendTimerDoneNotification(t(settings.language, 'rescue.timer_done'));
  }, [activeTimer, timerNow, settings.language]);

  useEffect(() => {
    void requestTimerNotificationsPermission();
  }, []);

  useEffect(() => {
    void refreshExactAlarmStatus();
  }, []);

  useEffect(() => {
    let dispose: (() => void) | null = null;
    void (async () => {
      dispose = await listenForTimerNotificationActions((action) => {
        if (action === 'done') {
          completeTimerFromNotificationRef.current();
          return;
        }
        if (action === 'snooze') {
          snoozeTimerFromNotificationRef.current();
        }
      });
    })();
    return () => {
      if (dispose) dispose();
    };
  }, []);

  useEffect(() => {
    let remove: (() => void) | undefined;
    void (async () => {
      if (!Capacitor.isNativePlatform()) return;
      try {
        const { App: CapacitorApp } = await import('@capacitor/app');
        const handle = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
          if (!isActive) return;
          void refreshExactAlarmStatus();
        });
        remove = () => {
          void handle.remove();
        };
      } catch {
        // no-op
      }
    })();
    return () => {
      if (remove) remove();
    };
  }, []);

  useEffect(() => {
    void syncReminderNotifications(settings.notificationStyle, {
      daily: settings.dailyReflectionPrompt,
      weekly: settings.weeklyPatternPrompt,
      language: settings.language
    });
  }, [settings.dailyReflectionPrompt, settings.language, settings.notificationStyle, settings.weeklyPatternPrompt]);

  useEffect(() => {
    if (settings.notificationStyle !== 'off' && settings.reentryNudges) return;
    void cancelNotification(REENTRY_REMINDER_NOTIFICATION_ID);
  }, [settings.notificationStyle, settings.reentryNudges]);

  useEffect(() => {
    const sessionRef = { current: '' };
    const openSession = async () => {
      const id = createId();
      sessionRef.current = id;
      await createAppSession({
        id,
        ts_start_ms: nowMs(),
        ts_end_ms: null,
        app_version: APP_VERSION,
        platform: navigator.userAgent.slice(0, 60)
      });
    };

    void openSession();

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (sessionRef.current) void endAppSession(sessionRef.current, nowMs());
        void maybeCloseIdle(nowMs(), true);
        return;
      }
      void openSession();
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (sessionRef.current) void endAppSession(sessionRef.current, nowMs());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onInteract = () => {
      maybeCloseIdle(nowMs(), false);
      lastInteractionRef.current = nowMs();
    };

    const int = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      const now = nowMs();
      if (now - lastInteractionRef.current >= IDLE_THRESHOLD_MS && idleAtRef.current === null) {
        idleAtRef.current = lastInteractionRef.current + IDLE_THRESHOLD_MS;
      }
    }, 10000);

    ['click', 'touchstart', 'keydown', 'scroll'].forEach((evt) => window.addEventListener(evt, onInteract, { passive: true }));

    return () => {
      window.clearInterval(int);
      ['click', 'touchstart', 'keydown', 'scroll'].forEach((evt) => window.removeEventListener(evt, onInteract));
    };
  }, []);

  async function maybeCloseIdle(ts: number, force: boolean): Promise<void> {
    if (idleAtRef.current === null) return;
    const start = idleAtRef.current;
    const end = ts;
    if (force || end - start >= IDLE_THRESHOLD_MS) {
      await createIdleSegment({
        id: createId(),
        ts_start_ms: start,
        ts_end_ms: end,
        source: 'in_app_no_interaction'
      });
    }
    idleAtRef.current = null;
  }

  async function refreshData(): Promise<void> {
    const [allPings, allRescues, allParking, scripts] = await Promise.all([
      listPings(),
      listRescues(),
      listParkingLotEntries(),
      listRescueScripts()
    ]);
    setPings(allPings);
    setRescues(allRescues);
    setParkingEntries(allParking);
    const next = { ...EMPTY_RESCUE_SCRIPTS } as Record<RescueKind, string>;
    for (const item of scripts) next[item.kind] = item.script ?? '';
    setRescueScripts(next);
  }

  function advanceHintLevel(next: number): void {
    const bounded = Math.max(hintLevel, next);
    setHintLevel(bounded);
    localStorage.setItem(UX_HINT_LEVEL_STORAGE_KEY, String(bounded));
  }

  function updateSetting<K extends keyof AppSettings>(key: K, value: AppSettings[K]): void {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }

  async function scheduleReentryReminder(): Promise<void> {
    if (settings.notificationStyle === 'off' || !settings.reentryNudges) {
      await cancelNotification(REENTRY_REMINDER_NOTIFICATION_ID);
      return;
    }
    await requestReminderNotificationsPermission();
    await cancelNotification(REENTRY_REMINDER_NOTIFICATION_ID);
    await scheduleReminderForStyle(
      REENTRY_REMINDER_NOTIFICATION_ID,
      Date.now() + REENTRY_REMINDER_DELAY_MS,
      tr('notifications.reentry_title'),
      tr('notifications.reentry_body'),
      settings.notificationStyle
    );
  }

  function maybeShowLandingIntroOnboarding(): void {
    if (!landingIntroFlowRef.current) return;
    landingIntroFlowRef.current = false;
    setOnboardingStep(0);
    setShowOnboarding(true);
  }

  async function handleSaveTinyWinFollowup(): Promise<void> {
    await saveTinyWin();
    maybeShowLandingIntroOnboarding();
    await scheduleReentryReminder();
  }

  async function dismissTinyWinFollowup(): Promise<void> {
    setTinyWinRescueId(null);
    setTinyWinPingId(null);
    setTinyWinDraft('');
    setTinyWinOutcome(null);
    maybeShowLandingIntroOnboarding();
    await scheduleReentryReminder();
  }

  async function refreshExactAlarmStatus(): Promise<void> {
    setExactAlarmStatus(await checkExactAlarmPermission());
  }

  async function handleOpenExactAlarmSettings(): Promise<void> {
    await openExactAlarmSettings();
    await refreshExactAlarmStatus();
  }

  function toggleStuckTimer(): void {
    if (frictionStartMs) {
      setFrictionStartMs(null);
      return;
    }
    setFrictionStartMs(nowMs());
    if (stuckTimerHelperCount >= 3) return;
    const next = stuckTimerHelperCount + 1;
    setStuckTimerHelperCount(next);
    localStorage.setItem(STUCK_TIMER_HELPER_COUNT_STORAGE_KEY, String(next));
  }

  completeTimerFromNotificationRef.current = () => {
    void completeTimer();
  };
  snoozeTimerFromNotificationRef.current = () => {
    void snoozeTimer(2);
  };
  const sheetPing = sheetPingId ? pings.find((p) => p.id === sheetPingId) ?? null : null;
  const sheetRecentContext = recentContextForPingType(pings, sheetPing?.type ?? null, sheetPingId);
  const sheetTopPick = sheetPing?.type
    ? personalizedTopPickRescueForFriction(pings, rescues, sheetPing.type, sheetPing.subtype, sheetPing.interruption_source)
    : null;
  const quickLogPing = quickLogPingId ? pings.find((p) => p.id === quickLogPingId) ?? null : null;
  const quickRecentContext = recentContextForPingType(pings, quickLogPing?.type ?? quickLogPingType ?? null, quickLogPingId);
  const quickTopPick = quickLogPing?.type
    ? personalizedTopPickRescueForFriction(pings, rescues, quickLogPing.type, quickLogPing.subtype, quickLogPing.interruption_source)
    : quickLogPingType
      ? personalizedTopPickRescueForFriction(pings, rescues, quickLogPingType, null, null)
      : null;

  async function exportJson(): Promise<void> {
    const payload = toJsonExport(await exportAllTables());
    downloadBlob('rethread-export.json', payload, 'application/json');
    void maybeShowInterstitialAd({
      enabled: settings.adsEnabled && effectiveTier === 'free',
      personalized: personalizedAdsAllowed,
      placement: 'export'
    });
  }

  async function exportPrivacyData(): Promise<void> {
    const payload = toJsonExport(await exportAllTables());
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    downloadBlob(`rethread-personal-data-${stamp}.json`, payload, 'application/json');
    setBackupStatus(tr('options.privacy_export_done'));
    setBackupStatusTone('success');
  }

  async function deleteAllData(): Promise<void> {
    const ok = window.confirm(tr('options.privacy_delete_confirm'));
    if (!ok) return;
    setSyncActivity(tr('options.sync_working_action', { action: tr('options.privacy_delete_data') }));
    try {
      await clearAllUserData();
      clearAppLocalState();
      setTab('log');
      setSelectedPing(null);
      setSelectedPingRescues([]);
      setSheetPingId(null);
      setQuickLogPingId(null);
      setQuickLogPingType(null);
      setActiveTimer(null);
      setFrictionStartMs(null);
      setUndoPingId(null);
      setUndoFrictionStartMs(null);
      setUndoUntil(0);
      setTinyWinRescueId(null);
      setTinyWinPingId(null);
      setTinyWinDraft('');
      setTinyWinOutcome(null);
      setParkingDraft('');
      setParkingOpen(false);
      setMetaSavedAt(0);
      setHintLevel(0);
      setStuckTimerHelperCount(0);
      setSettings(defaultSettings);
      setFavoriteRescues([]);
      setShowOnboarding(false);
      setShowLegalConsent(true);
      setShowDataDisclosure(false);
      setShowAdConsent(false);
      setPendingPersonalizedAds(false);
      setAdConsentStatus('not_required');
      setAdConsentProviderAvailable(false);
      setAdPrivacyOptionsAvailable(false);
      setLegalDoc(null);
      setOnboardingStep(0);
      resetOptionsState();
      setParkingEntries([]);
      setPings([]);
      setRescues([]);
      setBackupStatus(tr('options.privacy_delete_done'));
      setBackupStatusTone('success');
    } catch (error) {
      const message = error instanceof Error ? error.message : tr('options.privacy_delete_failed');
      setBackupStatus(message);
      setBackupStatusTone('error');
    } finally {
      await refreshData();
      setSyncActivity(null);
    }
  }

  async function openExternalUrl(url: string): Promise<void> {
    if (!url) return;
    try {
      if (Capacitor.isNativePlatform()) {
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url });
        return;
      }
    } catch {
      // fall through to window.open
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  async function exportCsv(): Promise<void> {
    downloadBlob('pings.csv', pingsToCsv(pings), 'text/csv');
    downloadBlob('rescues.csv', rescuesToCsv(rescues), 'text/csv');
    downloadBlob('parking_lot.csv', parkingLotToCsv(parkingEntries), 'text/csv');
    void maybeShowInterstitialAd({
      enabled: settings.adsEnabled && effectiveTier === 'free',
      personalized: personalizedAdsAllowed,
      placement: 'export'
    });
  }

  if (storageError) {
    return <div className="center">{`${tr('app.storage_unavailable')}: ${storageError}`}</div>;
  }

  return (
    <div className={`app theme-${settings.theme} style-${settings.visualStyle}${settings.compactUi ? ' compact-ui' : ''}`}>
      <header className="topbar">
        <div className="topbar-row">
          <div className="topbar-title">
            <h1>{tr('app.title')}</h1>
            <div className="build-stamp">{`v${APP_VERSION} · ${APP_BUILD_ID}`}</div>
          </div>
          <div className="topbar-actions">
            <button className="park-btn" onClick={() => setParkingOpen(true)}>
              {tr('topbar.park')}
            </button>
            <button className="park-btn" onClick={() => setTab('options')}>
              {tr('topbar.options')}
            </button>
          </div>
        </div>
      </header>

      <main>
        {tab === 'log' && (
          <section className={`log-flow-section${settings.simplifiedMode ? ' log-simple-section' : ''}`}>
            <div className="card log-flow-intro">
              <div className="log-flow-meta">
                {settings.simplifiedMode && <div className="simple-mode-title">{tr('log.simplified_badge')}</div>}
                {settings.showTips && hintLevel < 1 && <div className="log-flow-tip">{tr('log.tip')}</div>}
              </div>
              <div className="log-flow-title">{tr('log.flow_title')}</div>
              <div className="small sheet-lede">{tr('log.flow_body')}</div>
            </div>
            <div className="card log-stage-card">
              <div className="flow-step-label">{tr('log.flow_primary')}</div>
              <div className={`grid3${settings.simplifiedMode ? ' simple-grid' : ''}`}>
                {FRICTION_TYPES.map(({ type }) => (
                  <button key={type} className={`tile${settings.simplifiedMode ? ' simple-tile' : ''}`} onClick={() => void handlePing(type)}>
                    {pingTypeLabel(type)}
                  </button>
                ))}
              </div>
            </div>
            <div className="card log-stage-card log-stage-secondary">
              <div className="flow-step-label">{tr('log.flow_secondary')}</div>
              <div className="utility-row">
                <button
                  className={`secondary-btn stuck-timer-btn${frictionStartMs ? ' is-running' : ''}`}
                  onClick={toggleStuckTimer}
                >
                  {frictionStartMs
                    ? `${tr('log.stuck_prefix')} ${formatDuration(Math.floor((timerNow - frictionStartMs) / 1000))}`
                    : tr('log.start_stuck')}
                </button>
                <button className="quiet-btn" disabled={!undoPingId || nowMs() > undoUntil} onClick={() => void handleUndo()}>
                  {tr('log.undo')}
                </button>
              </div>
              {!frictionStartMs && stuckTimerHelperCount < 3 && (
                <div className="small slip-helper">
                  <span className="slip-helper-label">{tr('log.flow_timer_hint')}</span>{' '}
                  {tr('log.slip_helper')}
                </div>
              )}
            </div>
          </section>
        )}

        {tab === 'rescue' && (
          <section>
            {activeTimer ? (
              <div className="timer-card">
                <div className="small timer-kicker">{rescueKindText(activeTimer.kind)}</div>
                <div className="timer-big">{formatDuration(getRemainingSec(activeTimer, timerNow))}</div>
                {activeTimer.kind === 'breathe_90s' && (
                  <div className="timer-breathing-wrap">
                    <BreathingBox labels={rescueGuide('breathe_90s').breathing} />
                  </div>
                )}
                <div className="timer-buttons">
                  <button onClick={pauseResumeTimer}>{activeTimer.pausedAt === null ? tr('rescue.pause') : tr('rescue.resume')}</button>
                  <button onClick={() => void completeTimer(true)}>{tr('rescue.complete')}</button>
                  <button onClick={() => void snoozeTimer(2)}>{tr('rescue.snooze')}</button>
                  <button onClick={() => void cancelTimer()}>{tr('rescue.cancel')}</button>
                </div>
              </div>
            ) : (
              <div className="stack">
                {tinyWinRescueId && (
                  <div className={`card tiny-win-card${tinyWinOutcome ? ` is-${tinyWinOutcome}` : ''}`}>
                    <div className="row-title">{tr('rescue.what_helped')}</div>
                    <div className="small">{tr('rescue.followup_optional')}</div>
                    <div className="chips outcome-chips">
                      <button className={tinyWinOutcome === 'helped' ? 'active' : ''} onClick={() => setTinyWinOutcome('helped')}>
                        {tr('rescue.outcome_helped')}
                      </button>
                      <button className={tinyWinOutcome === 'a_bit' ? 'active' : ''} onClick={() => setTinyWinOutcome('a_bit')}>
                        {tr('rescue.outcome_a_bit')}
                      </button>
                      <button className={tinyWinOutcome === 'not_really' ? 'active' : ''} onClick={() => setTinyWinOutcome('not_really')}>
                        {tr('rescue.outcome_not_really')}
                      </button>
                    </div>
                    <textarea className="tiny-win-textarea" value={tinyWinDraft} maxLength={280} onChange={(e) => setTinyWinDraft(e.target.value)} />
                    <div className="timer-buttons tiny-win-actions">
                      {repeatMinutes !== null && (
                        <button onClick={() => void quickRepeatRescue()}>{tr('rescue.repeat_minutes', { minutes: repeatMinutes })}</button>
                      )}
                      <button
                        className="quiet-btn tiny-win-skip-inline"
                        onClick={() => void dismissTinyWinFollowup()}
                      >
                        {tr('rescue.skip')}
                      </button>
                    </div>
                    <button className="start-btn tiny-win-primary-btn" onClick={() => void handleSaveTinyWinFollowup()}>
                      {tr('rescue.log_tiny_win')}
                    </button>
                  </div>
                )}
                {tinyWinSavedAt > 0 && nowMs() - tinyWinSavedAt < 4000 && <div className="small tiny-win-saved-note">{tr('rescue.tiny_win_saved')}</div>}
                {settings.showTips && hintLevel < 2 && (
                  <div className="tip-banner">{tr('rescue.tip')}</div>
                )}
                <RescuePicker
                  scripts={rescueScripts}
                  defaultSound={settings.defaultRescueSound}
                  favoriteRescues={favoriteRescues}
                  onToggleFavorite={(kind) => setFavoriteRescues(toggleFavoriteRescue(kind))}
                  onSaveScript={(kind, script) => void saveRescueScript(kind, script)}
                  interruptionLabel={interruptionLabel}
                  rescueKindLabel={rescueKindText}
                  rescueGuide={rescueGuide}
                  subtypeLabel={subtypeLabel}
                  prepItemLabel={(key) => {
                    if (key === 'phoneSilenced') return tr('insights.phone_silenced');
                    if (key === 'tabsClosed') return tr('insights.tabs_closed');
                    if (key === 'docOpen') return tr('insights.doc_open');
                    return tr('insights.timer_visible');
                  }}
                  labels={{
                    whatsHappening: tr('rescuepicker.whats_happening'),
                    pulledAway: tr('rescuepicker.pulled_away'),
                    none: tr('common.none'),
                    chooseRescue: tr('rescuepicker.choose_rescue'),
                    contextStep: tr('rescuepicker.context_step'),
                    contextHint: tr('rescuepicker.context_hint'),
                    rescueStep: tr('rescuepicker.rescue_step'),
                    rescueHint: tr('rescuepicker.rescue_hint'),
                    supportStep: tr('rescuepicker.support_step'),
                    supportHint: tr('rescuepicker.support_hint'),
                    topPick: tr('log.quick_top_pick'),
                    favorites: tr('rescuepicker.favorites'),
                    recentContextHint: tr('rescuepicker.recent_context_hint'),
                    applyLastContext: tr('rescuepicker.apply_last_context'),
                    start: tr('rescuepicker.start'),
                    prepChecklist: tr('rescuepicker.prep_checklist'),
                    resetTip: tr('rescuepicker.reset_tip'),
                    optionalDetails: tr('rescuepicker.optional_details'),
                    smallestStepPlaceholder: tr('rescuepicker.smallest_step'),
                    start10mWithNote: tr('rescuepicker.start_10m_note'),
                    sentencePlaceholder: tr('rescuepicker.sentence_placeholder'),
                    saveSentence: tr('rescuepicker.save_sentence'),
                    focusSound: tr('rescuepicker.focus_sound'),
                    editRescueText: tr('rescuepicker.edit_text'),
                    saveScript: tr('rescuepicker.save_script'),
                    savedSuffix: tr('common.saved'),
                    favoriteOn: tr('rescuepicker.favorite_on'),
                    favoriteOff: tr('rescuepicker.favorite_off')
                  }}
                  onStart={(kind, note, sound, prep) => void startRescue(kind, null, note, sound, prep)}
                />
              </div>
            )}
          </section>
        )}

        {tab === 'history' && (
          <HistoryTab
            historyRange={historyRange}
            onChangeHistoryRange={setHistoryRange}
            typeFilters={typeFilters}
            onToggleTypeFilter={(type) =>
              setTypeFilters((prev) => (prev.includes(type) ? prev.filter((x) => x !== type) : [...prev, type]))
            }
            allTypes={FRICTION_TYPES.map((item) => item.type)}
            filteredHistory={filteredHistory}
            rescues={rescues}
            onOpenPing={(ping) => void openPingDetail(ping)}
            rangeLabel={rangeLabel}
            pingTypeLabel={pingTypeLabel}
            subtypeLabel={subtypeLabel}
            interruptionLabel={interruptionLabel}
            rescueUsedLabel={tr('history.rescue_used')}
            emptyLabel={tr('history.empty')}
          />
        )}

        {tab === 'insights' && (
          <InsightsTab
            pings={pings}
            rescues={rescues}
            parkingEntries={parkingEntries}
            insightRange={insightRange}
            onChangeInsightRange={setInsightRange}
            bucketInfo={bucketInfo}
            onSelectBucket={setBucketInfo}
            pingTypeLabel={pingTypeLabel}
            rescueKindLabel={rescueKindText}
            interruptionLabel={interruptionLabel}
            subtypeLabel={subtypeLabel}
            onExportJson={() => void exportJson()}
            onExportCsv={() => void exportCsv()}
            isPro={effectiveTier === 'pro'}
            onOpenUpgrade={() => setTab('options')}
            rangeLabel={rangeLabel}
            dayLabel={appDayLabel}
            labels={insightsLabels}
          />
        )}

        {tab === 'options' && (
          <OptionsTab
            settings={settings}
            activeThemeDescription={activeThemeDescription}
            activeVisualStyleDescription={activeVisualStyleDescription}
            activeFontDescription={activeFontDescription}
            isWebApp={!Capacitor.isNativePlatform()}
            labels={optionsLabels}
            onUpdateSetting={updateSetting}
            exactAlarmStatus={exactAlarmStatus}
            onOpenExactAlarmSettings={handleOpenExactAlarmSettings}
            adConsentStatus={adConsentStatus}
            adPrivacyOptionsAvailable={adPrivacyOptionsAvailable}
            onUpdateAdPersonalization={updateAdPersonalization}
            onWithdrawAdConsent={withdrawAdConsent}
            onOpenAdPrivacyOptions={openAdPrivacyOptions}
            billingConfigured={billingEnabled}
            billingTier={effectiveTier}
            billingStatusText={billingStatusText}
            onCheckBilling={checkBilling}
            onRestoreBilling={restoreBilling}
            canManageBilling={canManageBilling}
            onManageBilling={manageBilling}
            billingDiagnostics={billingDiagnostics}
            onClearBillingEvents={clearBillingDiagnosticsEvents}
            onRunBillingDiagnostics={runBillingDiagnostics}
            monetizationPlans={monetizationPlans}
            onBuyPlan={buyPlan}
            onCreateBackup={createBackup}
            onRestoreBackup={restoreBackup}
            onConnectDrive={connectDrive}
            onDisconnectDrive={disconnectDrive}
            onUploadDriveBackup={uploadDriveBackup}
            onRestoreDriveBackup={restoreDriveBackup}
            driveConfigured={driveConfigured}
            driveConnected={driveConnected}
            syncActivity={syncActivity}
            backupStatus={backupStatus}
            backupStatusTone={backupStatusTone}
            onReplayOnboarding={replayOnboarding}
            onOpenPrivacyPolicy={() => openLegalDoc('privacy')}
            onOpenTerms={() => openLegalDoc('terms')}
            onContactSupport={() => {
              const email = import.meta.env.VITE_SUPPORT_EMAIL ?? 'support@example.com';
              return openExternalUrl(`mailto:${email}`);
            }}
            onExportData={exportPrivacyData}
            onDeleteAllData={deleteAllData}
          />
        )}
      </main>

      <AppBottomNav
        tab={tab}
        onChange={setTab}
        labels={bottomNavLabels}
      />

      {sheetPingId && (
        <PingRescueSheet
          pingId={sheetPingId}
          pingType={sheetPing?.type ?? null}
          topPickKind={sheetTopPick}
          subtype={sheetPing?.subtype ?? null}
          interruptionSource={sheetPing?.interruption_source ?? null}
          scripts={rescueScripts}
          defaultSound={settings.defaultRescueSound}
          favoriteRescues={favoriteRescues}
          recentContext={sheetRecentContext}
          showSavedHint={metaSavedAt > 0 && nowMs() - metaSavedAt < 2500}
          labels={pingRescueSheetLabels}
          onClose={() => setSheetPingId(null)}
          onSaveScript={(kind, script) => void saveRescueScript(kind, script)}
          onToggleFavorite={(kind) => setFavoriteRescues(toggleFavoriteRescue(kind))}
          interruptionLabel={interruptionLabel}
          rescueKindLabel={rescueKindText}
          rescueGuide={rescueGuide}
          subtypeLabel={subtypeLabel}
          prepItemLabel={prepItemLabel}
          rescuePickerLabels={rescuePickerLabels}
          onSetSubtype={(subtype) => void setSubtypeForPing(sheetPingId, subtype)}
          onSetInterruption={(source) => void setInterruptionForPing(sheetPingId, source)}
          onApplyRecentContext={(context) => {
            void setSubtypeForPing(sheetPingId, context.subtype);
            void setInterruptionForPing(sheetPingId, context.interruptionSource);
          }}
          onStart={(kind, pingId, note, sound, prep) => void startRescue(kind, pingId, note, sound, prep)}
        />
      )}

      {quickLogPingId && (
        <QuickLogSheet
          pingType={quickLogPing?.type ?? quickLogPingType}
          subtype={quickLogPing?.subtype ?? null}
          topPickKind={quickTopPick}
          favoriteRescues={favoriteRescues}
          recentContext={quickRecentContext}
          rescueKindLabel={rescueKindText}
          rescueGuide={rescueGuide}
          labels={quickLogLabels}
          subtypeLabel={subtypeLabel}
          interruptionLabel={interruptionLabel}
          onClose={() => {
            setQuickLogPingId(null);
            setQuickLogPingType(null);
          }}
          onSetSubtype={(subtype) => {
            if (!quickLogPingId) return;
            void setSubtypeForPing(quickLogPingId, subtype);
          }}
          onApplyRecentContext={(context) => {
            if (!quickLogPingId) return;
            void setSubtypeForPing(quickLogPingId, context.subtype);
            void setInterruptionForPing(quickLogPingId, context.interruptionSource);
          }}
          onStartRescue={(kind, note) => {
            if (!quickLogPingId) return;
            void startRescue(kind, quickLogPingId, note ?? null, false, {
              phoneSilenced: false,
              tabsClosed: false,
              docOpen: false,
              timerVisible: true
            });
            setQuickLogPingId(null);
            setQuickLogPingType(null);
          }}
        />
      )}

      {selectedPing && (
        <PingDetailModal
          ping={selectedPing}
          rescues={selectedPingRescues}
          pingTypeLabel={pingTypeLabel}
          rescueKindLabel={rescueKindText}
          interruptionLabel={(source) => (source ? interruptionLabel(source) : tr('common.na'))}
          labels={pingDetailLabels}
          onClose={() => setSelectedPing(null)}
          onDelete={() => void deleteSelectedPing()}
          onSaveNote={(note) => void addNoteToSelected(note)}
        />
      )}

      {parkingOpen && (
        <ParkingLotModal
          draft={parkingDraft}
          labels={parkingLabels}
          onChangeDraft={setParkingDraft}
          onClose={() => setParkingOpen(false)}
          onSave={() => void saveParkingLot()}
        />
      )}

      <ConsentFlow
        showOnboarding={showOnboarding}
        showDataDisclosure={showDataDisclosure}
        showAdConsent={showAdConsent}
        showLegalConsent={showLegalConsent}
        legalDoc={legalDoc}
        onboardingStep={onboardingStep}
        onboardingSteps={steps}
        labels={consentLabels}
        onChangeOnboardingStep={setOnboardingStep}
        onSkipOnboarding={completeOnboarding}
        onCompleteOnboarding={completeOnboarding}
        onAcceptDisclosure={completeDataDisclosure}
        onExitConsent={() => {
          void exitFromLegalConsent();
        }}
        onAllowAds={allowPersonalizedAds}
        onKeepNonPersonalizedAds={keepNonPersonalizedAds}
        onOpenPrivacy={() => {
          openLegalDoc('privacy');
        }}
        onOpenTerms={() => {
          openLegalDoc('terms');
        }}
        onAcceptLegal={completeLegalConsent}
        onCloseLegalDoc={closeLegalDoc}
        onOpenExternalLegal={() => {
          void openExternalUrl(
            legalDoc === 'privacy'
              ? import.meta.env.VITE_PRIVACY_POLICY_URL ?? 'https://example.com/privacy'
              : import.meta.env.VITE_TERMS_URL ?? 'https://example.com/terms'
          );
        }}
      />
    </div>
  );
}

function getRemainingSec(t: ActiveTimer, now: number): number {
  const paused = t.pausedAt ? now - t.pausedAt : 0;
  const elapsed = now - t.startedAt - t.pausedAccumulatedMs - paused;
  return Math.max(0, t.durationSec - Math.floor(elapsed / 1000));
}

async function syncReminderNotifications(
  notificationStyle: NotificationStyle,
  options: {
    daily: boolean;
    weekly: boolean;
    language: AppLanguage;
  }
): Promise<void> {
  if (notificationStyle === 'off') {
    await cancelNotification(DAILY_REFLECTION_NOTIFICATION_ID);
    await cancelNotification(WEEKLY_PATTERN_NOTIFICATION_ID);
    return;
  }

  if (options.daily || options.weekly) {
    await requestReminderNotificationsPermission();
  }

  if (options.daily) {
    await scheduleReminderForStyle(
      DAILY_REFLECTION_NOTIFICATION_ID,
      nextLocalTime(20, 0).getTime(),
      t(options.language, 'notifications.daily_title'),
      t(options.language, 'notifications.daily_body'),
      notificationStyle
    );
  } else {
    await cancelNotification(DAILY_REFLECTION_NOTIFICATION_ID);
  }

  if (options.weekly) {
    await scheduleReminderForStyle(
      WEEKLY_PATTERN_NOTIFICATION_ID,
      nextLocalWeekdayTime(0, 18, 0).getTime(),
      t(options.language, 'notifications.weekly_title'),
      t(options.language, 'notifications.weekly_body'),
      notificationStyle
    );
  } else {
    await cancelNotification(WEEKLY_PATTERN_NOTIFICATION_ID);
  }
}

async function scheduleReminderForStyle(
  notificationId: number,
  atMs: number,
  title: string,
  body: string,
  style: NotificationStyle
): Promise<void> {
  if (style === 'off') return;
  await scheduleReminderNotification(notificationId, atMs, title, body, style);
}

function nextLocalTime(hour: number, minute: number): Date {
  const next = new Date();
  next.setHours(hour, minute, 0, 0);
  if (next.getTime() <= Date.now()) {
    next.setDate(next.getDate() + 1);
  }
  return next;
}

function nextLocalWeekdayTime(dayOfWeek: number, hour: number, minute: number): Date {
  const next = new Date();
  next.setHours(hour, minute, 0, 0);
  const delta = (dayOfWeek - next.getDay() + 7) % 7;
  next.setDate(next.getDate() + delta);
  if (delta === 0 && next.getTime() <= Date.now()) {
    next.setDate(next.getDate() + 7);
  }
  return next;
}
